import { useEffect, useState } from "react";
import { Send, Upload } from "lucide-react";
import { toast } from "sonner";

const API_ERROR = "We couldn't submit your enquiry right now. Please try again or contact us on WhatsApp.";
const DIAL_CODES = [
  ["United States / Canada", "+1"], ["United Kingdom", "+44"], ["India", "+91"], ["Saudi Arabia", "+966"], ["United Arab Emirates", "+971"], ["Qatar", "+974"], ["Bahrain", "+973"], ["Kuwait", "+965"], ["Oman", "+968"], ["Jordan", "+962"], ["Lebanon", "+961"], ["Iraq", "+964"], ["Israel", "+972"], ["Turkey", "+90"], ["Germany", "+49"], ["France", "+33"], ["Italy", "+39"], ["Spain", "+34"], ["Netherlands", "+31"], ["Belgium", "+32"], ["Switzerland", "+41"], ["Austria", "+43"], ["Sweden", "+46"], ["Norway", "+47"], ["Denmark", "+45"], ["Poland", "+48"], ["Greece", "+30"], ["Romania", "+40"], ["Russia", "+7"], ["China", "+86"], ["Japan", "+81"], ["South Korea", "+82"], ["Singapore", "+65"], ["Malaysia", "+60"], ["Indonesia", "+62"], ["Thailand", "+66"], ["Vietnam", "+84"], ["Philippines", "+63"], ["Australia", "+61"], ["New Zealand", "+64"], ["Brazil", "+55"], ["Mexico", "+52"], ["Peru", "+51"], ["South Africa", "+27"], ["Egypt", "+20"], ["Morocco", "+212"], ["Algeria", "+213"], ["Tunisia", "+216"], ["Sri Lanka", "+94"], ["Pakistan", "+92"], ["Iran", "+98"]
];
const UNITS = ["kg", "MT", "tonnes", "pieces", "lots", "Other"];
const emailOk = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(value || "").trim());
const phoneOk = (dial, number) => {
  if (!dial) return false;
  const clean = String(number || "").trim();
  if (!/^[0-9\s\-()]+$/.test(clean)) return false;
  const digits = clean.replace(/\D/g, "");
  return digits.length >= 5 && digits.length <= 15;
};
const quantityOk = (value) => /^\d+(\.\d+)?$/.test(String(value || "").trim()) && Number(value) > 0;

async function postLead(path, payload) {
  const response = await fetch(path, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
  let data = null;
  try { data = await response.json(); } catch { data = null; }
  if (!response.ok || !data || data.success !== true) throw new Error(data?.detail || API_ERROR);
  return data;
}

function ErrorText({ children }) { return children ? <span className="field-error" role="alert">{children}</span> : null; }

function PhoneFields({ errors, prefix }) {
  return <label>WhatsApp / phone *<span className="phone-field"><select name="dialCode" defaultValue="" required data-testid={`${prefix}-dial-code`}><option value="">Dial code</option>{DIAL_CODES.map(([country, code]) => <option key={`${country}-${code}`} value={code}>{country} {code}</option>)}</select><input name="phoneNumber" inputMode="tel" autoComplete="tel-national" placeholder="Phone number" required data-testid={`${prefix}-phone-input`} /></span><ErrorText>{errors.phone}</ErrorText></label>;
}

function QuantityUnitFields({ errors, prefix }) {
  const [unit, setUnit] = useState("");
  return <label>Quantity + unit *<span className="quantity-unit"><input name="quantity" inputMode="decimal" placeholder="500" required data-testid={`${prefix}-quantity-input`} /><select name="unit" value={unit} onChange={(event) => setUnit(event.target.value)} required data-testid={`${prefix}-unit-select`}><option value="">Unit</option>{UNITS.map((item) => <option key={item} value={item}>{item}</option>)}</select></span>{unit === "Other" && <input className="unit-other" name="unitOther" placeholder="Specify unit" required data-testid={`${prefix}-unit-other`} />}<ErrorText>{errors.quantity || errors.unit}</ErrorText></label>;
}

export function RfqForm({ selectedProduct = "", initialMode = "buy", compact = false, idPrefix = "rfq" }) {
  const [mode, setMode] = useState(initialMode);
  const [product, setProduct] = useState(selectedProduct);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});
  const tid = (name) => `${idPrefix}-${name}`;

  useEffect(() => { setProduct(selectedProduct); }, [selectedProduct]);
  useEffect(() => { setMode(initialMode); setSent(false); setErrors({}); }, [initialMode]);

  const validate = (data) => {
    const next = {};
    ["name", "country", "product"].forEach((key) => { if (!String(data[key] || "").trim()) next[key] = "This field is required."; });
    if (!emailOk(data.email)) next.email = "Enter a valid email address.";
    if (!phoneOk(data.dialCode, data.phoneNumber)) next.phone = "Enter a valid phone number.";
    if (!quantityOk(data.quantity)) next.quantity = "Enter a valid quantity greater than 0.";
    if (!String(data.unit || "").trim()) next.unit = "This field is required.";
    if (data.unit === "Other" && !String(data.unitOther || "").trim()) next.unit = "This field is required.";
    if (mode === "sell") {
      if (!String(data.location || "").trim()) next.location = "This field is required.";
      if (!String(data.condition || "").trim()) next.condition = "This field is required.";
    }
    return next;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSent(false);
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    const nextErrors = validate(data);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) { toast.error("Please fix the highlighted fields."); return; }
    const unit = data.unit === "Other" ? data.unitOther : data.unit;
    const phone = `${data.dialCode} ${String(data.phoneNumber).trim()}`;
    const payload = {
      formType: "rfq",
      buySell: mode === "sell" ? "Sell" : "Buy",
      name: data.name,
      company: data.company || "",
      country: data.country,
      email: data.email,
      phone,
      product,
      gradeSpecification: data.grade || "",
      quantity: Number(data.quantity),
      unit,
      materialLocation: mode === "sell" ? data.location || "" : data.location || "",
      destination: mode === "buy" ? data.destination || "" : "",
      conditionDescription: mode === "sell" ? data.condition || "" : "",
      additionalNotes: data.notes || "",
      message: mode === "sell" ? [data.condition, data.notes].filter(Boolean).join("\n") : data.requirement || data.notes || "",
      sourcePage: window.location.pathname,
    };
    setSubmitting(true);
    try {
      await postLead("/api/enquiries", payload);
      setSent(true);
      toast.success("Requirement received — SJB Steels will be in touch shortly.");
    } catch (requestError) {
      console.warn("Enquiry failed", requestError);
      setErrors({ api: API_ERROR });
      toast.error(API_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className={`rfq-form lead-form ${compact ? "rfq-form-compact" : ""}`} onSubmit={submit} noValidate data-testid={tid("form")}>
      <div className="toggle-row" role="group" aria-label="Requirement type">
        <button type="button" className={mode === "buy" ? "toggle-active" : ""} onClick={() => { setMode("buy"); setSent(false); setErrors({}); }} data-testid={tid("buy-toggle")}>I want to buy</button>
        <button type="button" className={mode === "sell" ? "toggle-active" : ""} onClick={() => { setMode("sell"); setSent(false); setErrors({}); }} data-testid={tid("sell-toggle")}>I want to sell</button>
      </div>
      <div className="form-grid">
        <label>Name *<input name="name" placeholder="Your name" required data-testid={tid("name-input")} /><ErrorText>{errors.name}</ErrorText></label>
        <label>Company<input name="company" placeholder="Company name" data-testid={tid("company-input")} /></label>
        <label>Country *<input name="country" placeholder="Country" required data-testid={tid("country-input")} /><ErrorText>{errors.country}</ErrorText></label>
        <label>Email *<input name="email" type="email" placeholder="you@company.com" required data-testid={tid("email-input")} /><ErrorText>{errors.email}</ErrorText></label>
        <PhoneFields errors={errors} prefix={tid("rfq")} />
        <label>Material *<input name="product" value={product} onChange={(event) => setProduct(event.target.value)} placeholder="Material or product" required data-testid={tid("product-input")} /><ErrorText>{errors.product}</ErrorText></label>
        <label>Grade / specification<input name="grade" placeholder="Grade, form or details" data-testid={tid("grade-input")} /></label>
        <QuantityUnitFields errors={errors} prefix={tid("rfq")} />
        {mode === "sell" ? <label>Material location *<input name="location" placeholder="City / country" required data-testid={tid("location-input")} /><ErrorText>{errors.location}</ErrorText></label> : <label>Destination<input name="destination" placeholder="Destination" data-testid={tid("destination-input")} /></label>}
      </div>
      {mode === "sell" ? (
        <>
          <label className="full-label">Condition / description *<textarea name="condition" rows="4" placeholder="Condition, quantity available, photos or documents you can share..." required data-testid={tid("condition-input")} /><ErrorText>{errors.condition}</ErrorText></label>
          <label className="full-label">Additional notes<textarea name="notes" rows="3" placeholder="Anything else we should know..." data-testid={tid("notes-input")} /></label>
        </>
      ) : (
        <label className="full-label">Additional requirements<textarea name="requirement" rows="4" placeholder="Tell us more about your requirement..." data-testid={tid("requirement-input")} /></label>
      )}
      <label className="upload-field upload-disabled" data-testid={tid("upload-field")}><Upload size={15} /> Upload material images / documents <span>File upload coming soon</span><input type="file" name="attachment" accept=".jpg,.jpeg,.png,.pdf" disabled data-testid={tid("file-input")} /></label>
      {errors.api && <p className="rfq-error" data-testid={tid("error-message")}>{errors.api}</p>}
      {sent && <p className="form-success" data-testid={tid("success-message")}>Requirement received — SJB Steels will be in touch shortly.</p>}
      <button className="button copper-button submit-button" type="submit" disabled={submitting} data-testid={tid("submit-button")}>{submitting ? "Sending..." : sent ? (mode === "sell" ? "Material details sent ✓" : "Requirement sent ✓") : (mode === "sell" ? "Submit material details" : "Submit requirement")} <Send size={16} /></button>
      <p className="form-note">Attachments are optional. By submitting, you agree to be contacted regarding this enquiry.</p>
    </form>
  );
}

export function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSent(false);
    const data = Object.fromEntries(new FormData(event.currentTarget).entries());
    const next = {};
    if (!String(data.name || "").trim()) next.name = "This field is required.";
    if (!emailOk(data.email)) next.email = "Enter a valid email address.";
    if (!phoneOk(data.dialCode, data.phoneNumber)) next.phone = "Enter a valid phone number.";
    if (!String(data.message || "").trim()) next.message = "This field is required.";
    setErrors(next);
    if (Object.keys(next).length) { toast.error("Please fix the highlighted fields."); return; }
    const payload = { formType: "contact", name: data.name, company: data.company || "", country: data.country || "", email: data.email, phone: `${data.dialCode} ${String(data.phoneNumber).trim()}`, subject: data.subject || "", message: data.message, sourcePage: window.location.pathname };
    setSubmitting(true);
    try {
      await postLead("/api/contact-enquiries", payload);
      setSent(true);
      toast.success("Thank you — your message has been received.");
    } catch (requestError) {
      console.warn("Contact failed", requestError);
      setErrors({ api: API_ERROR });
      toast.error(API_ERROR);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form className="lead-form" onSubmit={submit} noValidate data-testid="contact-form">
      <div className="form-grid">
        <label>Name *<input name="name" placeholder="Your name" required data-testid="contact-name-input" /><ErrorText>{errors.name}</ErrorText></label>
        <label>Company<input name="company" placeholder="Company name" data-testid="contact-company-input" /></label>
        <label>Country<input name="country" placeholder="Country" data-testid="contact-country-input" /></label>
        <label>Email *<input name="email" type="email" placeholder="you@company.com" required data-testid="contact-email-input" /><ErrorText>{errors.email}</ErrorText></label>
        <PhoneFields errors={errors} prefix="contact" />
        <label>Subject<input name="subject" placeholder="How can we help?" data-testid="contact-subject-input" /></label>
      </div>
      <label className="full-label">Message *<textarea name="message" rows="6" placeholder="Tell us more..." required data-testid="contact-message-input" /><ErrorText>{errors.message}</ErrorText></label>
      {errors.api && <p className="rfq-error" data-testid="contact-error-message">{errors.api}</p>}
      {sent && <p className="form-success" data-testid="contact-success-message">Thank you — your message has been received.</p>}
      <button className="button copper-button submit-button" type="submit" disabled={submitting} data-testid="contact-submit-button">{submitting ? "Sending..." : sent ? "Message sent ✓" : "Send message"} <Send size={16} /></button>
    </form>
  );
}
